import React, { useMemo } from 'react';
import { GenericSearchSelect, SelectableItem } from '../../../../../components/library/info-order/OrderModules/components/GenericSearchSelect';
import { FacturacionTarifas } from '../../../../../types/types.facturacion';

// Interfaz específica para estudios de cotizaciones
export interface QuotationStudyItem extends SelectableItem {
  id: number;
  code: string;
  name: string;
  group: string;
  price?: number;
  isActive?: boolean;
  // Campos adicionales específicos de FacturacionTarifas
  Tf_Es_Id: number;
  Tf_Es_Codigo: string;
  Tf_Es_Nombre: string;
  Tf_Aa_Nombre: string;
  Tf_ValorTarifa: number;
  originalData?: FacturacionTarifas;
}

interface QuotationStudiesSearchSelectProps {
  dataSource: FacturacionTarifas[];
  onStudySelected: (study: QuotationStudyItem) => void;
  selectedStudies?: QuotationStudyItem[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  minSearchLength?: number;
}

export const QuotationStudiesSearchSelect: React.FC<QuotationStudiesSearchSelectProps> = ({
  dataSource = [],
  onStudySelected,
  selectedStudies = [],
  placeholder = 'Buscar estudios...',
  label = 'Buscar estudios',
  disabled = false,
  className = '',
  minSearchLength = 1,
}) => {
  // Crear el dataSource para el componente genérico con lazy loading
  const genericDataSource = {
    load: async (options?: { skip?: number; take?: number; searchText?: string }): Promise<QuotationStudyItem[]> => {
      const { skip = 0, take = 200, searchText = '' } = options || {};
      
      // Filtrar estudios por texto de búsqueda si se proporciona
      let filteredStudies = dataSource;
      if (searchText && searchText.length >= minSearchLength) {
        const search = searchText.toLowerCase();
        filteredStudies = dataSource.filter(study =>
          study.Estudio?.Es_Nombre.toLowerCase().includes(search) ||
          study.Estudio?.Es_Codigo.toLowerCase().includes(search) ||
          study.Estudio?.SysA_AreasAnalisis?.Aa_Nombre.toLowerCase().includes(search)
        );
      }
      
      // Simular paginación
      const paginatedStudies = filteredStudies.slice(skip, skip + take);
      
      return paginatedStudies.map(study => ({
        id: study.Tf_Es_Id,
        code: study.Estudio?.Es_Codigo || `E${study.Tf_Es_Id}`,
        name: study.Estudio?.Es_Nombre || 'Estudio sin nombre',
        group: study.Estudio?.SysA_AreasAnalisis?.Aa_Nombre || 'Sin área',
        price: study.Tf_Valor,
        isActive: study.Tf_Estado,
        // Campos específicos para compatibilidad
        Tf_Es_Id: study.Tf_Es_Id,
        Tf_Es_Codigo: study.Estudio?.Es_Codigo || '',
        Tf_Es_Nombre: study.Estudio?.Es_Nombre || '',
        Tf_Aa_Nombre: study.Estudio?.SysA_AreasAnalisis?.Aa_Nombre || '',
        Tf_ValorTarifa: study.Tf_Valor,
        originalData: study
      }));
    },
    
    getTotalCount: async (searchText?: string): Promise<number> => {
      if (!searchText || searchText.length < minSearchLength) {
        return dataSource.length;
      }
      
      const search = searchText.toLowerCase();
      const filteredStudies = dataSource.filter(study =>
        study.Estudio?.Es_Nombre.toLowerCase().includes(search) ||
        study.Estudio?.Es_Codigo.toLowerCase().includes(search) ||
        study.Estudio?.SysA_AreasAnalisis?.Aa_Nombre.toLowerCase().includes(search)
      );
      
      return filteredStudies.length;
    }
  };

  // Manejar selección de estudio
  const handleItemSelected = (item: SelectableItem) => {
    const studyItem: QuotationStudyItem = {
      id: typeof item.id === 'string' ? parseInt(item.id) : item.id,
      code: item.code || '',
      name: item.name,
      group: item.group || 'Estudios',
      price: (item as any).price,
      isActive: (item as any).isActive,
      Tf_Es_Id: (item as any).Tf_Es_Id,
      Tf_Es_Codigo: (item as any).Tf_Es_Codigo,
      Tf_Es_Nombre: (item as any).Tf_Es_Nombre,
      Tf_Aa_Nombre: (item as any).Tf_Aa_Nombre,
      Tf_ValorTarifa: (item as any).Tf_ValorTarifa,
      originalData: (item as any).originalData,
    };
    onStudySelected(studyItem);
  };

  return (
    <GenericSearchSelect
      dataSource={genericDataSource}
      onItemSelected={handleItemSelected}
      selectedItems={selectedStudies}
      placeholder={placeholder}
      label={label}
      disabled={disabled}
      className={className}
      searchFields={['name', 'code']}
      groupField="group"
      displayFields={{
        title: 'name',
        subtitle: 'code'
      }}
      showGroups={true}
      minSearchLength={minSearchLength}
    />
  );
};
